(() => {
  const DURATION = 500;
  const easeOutQuint = (t) => 1 + --t * t * t * t * t;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /*
    Slider used by the image carousel, image grid (mobile) and related products.
    data-config: JSON map of media query -> { perView: number | "auto", spacing: px, loop: bool, disabled: bool }.
    "all" is the base; later matching queries override it (like keen-slider breakpoints).
    perView "auto" keeps each slide's CSS width.
  */
  if (!customElements.get('custom-page-slider')) {
    customElements.define(
      'custom-page-slider',
      class CustomPageSlider extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-cxp-track]');
          this.slides = Array.from(this.querySelectorAll('[data-cxp-slide]'));
          if (!this.track || !this.slides.length) return;
          try {
            this.configs = JSON.parse(this.dataset.config || '{}');
          } catch (error) {
            this.configs = {};
          }
          this.index = 0;
          this.offset = 0;
          this.bar = this.querySelector('[data-cxp-progress]');
          this.querySelectorAll('[data-cxp-prev]').forEach((b) => b.addEventListener('click', () => this.go(-1)));
          this.querySelectorAll('[data-cxp-next]').forEach((b) => b.addEventListener('click', () => this.go(1)));
          this.addEventListener('keydown', (event) => {
            if (event.target.closest('input, textarea')) return;
            if (event.key === 'ArrowLeft') this.go(-1);
            if (event.key === 'ArrowRight') this.go(1);
          });
          this.initDrag();
          this.resizeObserver = new ResizeObserver(() => this.layout());
          this.resizeObserver.observe(this);
          this.layout();
        }

        disconnectedCallback() {
          this.resizeObserver?.disconnect();
          cancelAnimationFrame(this.frame);
        }

        options() {
          const merged = { perView: 1, spacing: 0, loop: false, disabled: false };
          Object.entries(this.configs).forEach(([query, value]) => {
            if (query === 'all' || window.matchMedia(query).matches) Object.assign(merged, value);
          });
          return merged;
        }

        layout() {
          const opts = this.options();
          this.opts = opts;
          this.size = this.track.getBoundingClientRect().width || 1;
          this.classList.toggle('is-disabled', opts.disabled);
          if (opts.disabled) {
            this.slides.forEach((slide) => {
              slide.style.removeProperty('min-width');
              slide.style.removeProperty('max-width');
              slide.style.removeProperty('transform');
              slide.style.removeProperty('margin-right');
            });
            return;
          }
          if (opts.perView !== 'auto') {
            const width = (this.size - opts.spacing * (opts.perView - 1)) / opts.perView;
            this.slides.forEach((slide) => {
              slide.style.minWidth = `${width}px`;
              slide.style.maxWidth = `${width}px`;
            });
          } else {
            this.slides.forEach((slide) => {
              slide.style.removeProperty('min-width');
              slide.style.removeProperty('max-width');
            });
          }
          this.widths = this.slides.map((slide) => slide.getBoundingClientRect().width);
          this.starts = [];
          let x = 0;
          this.widths.forEach((w) => {
            this.starts.push(x);
            x += w + opts.spacing;
          });
          this.total = x - opts.spacing;
          this.loop = opts.loop && this.total > this.size + Math.max(...this.widths);
          this.maxOffset = Math.max(0, this.total - this.size);
          if (!this.loop) this.offset = Math.min(this.offset, this.maxOffset);
          this.render();
        }

        // Offset of slide i, clamped so the last slides align to the end (keen-slider "origin: auto").
        target(i) {
          if (this.loop) {
            const count = this.slides.length;
            const lap = Math.floor(i / count);
            const rel = ((i % count) + count) % count;
            return lap * (this.total + this.opts.spacing) + this.starts[rel];
          }
          return Math.min(this.starts[Math.max(0, Math.min(this.slides.length - 1, i))], this.maxOffset);
        }

        render() {
          const spacing = this.opts.spacing;
          const cycle = this.total + spacing;
          let natural = 0;
          this.slides.forEach((slide, i) => {
            let x = this.starts[i] - this.offset;
            if (this.loop) {
              // Wrap like keen-slider with origin "auto": one slide waits on the left, the rest follow on the right.
              const low = -(Math.max(...this.widths) + spacing);
              x = ((((x - low) % cycle) + cycle) % cycle) + low;
            }
            slide.style.transform = `translate3d(${x - natural}px, 0px, 0px)`;
            natural += this.widths[i];
          });
          if (this.bar) {
            const ratio = Math.min(1, this.size / this.total);
            const progress = this.maxOffset ? this.offset / this.maxOffset : 0;
            this.bar.style.width = `${ratio * 100}%`;
            this.bar.style.left = `${Math.max(0, Math.min(1, progress)) * (1 - ratio) * 100}%`;
          }
        }

        nearest(offset) {
          if (this.loop) {
            const count = this.slides.length;
            const cycle = this.total + this.opts.spacing;
            const lap = Math.floor(offset / cycle);
            let best = 0;
            let dist = Infinity;
            for (let l = lap - 1; l <= lap + 1; l++) {
              for (let i = 0; i < count; i++) {
                const d = Math.abs(l * cycle + this.starts[i] - offset);
                if (d < dist) {
                  dist = d;
                  best = l * count + i;
                }
              }
            }
            return best;
          }
          let best = 0;
          let dist = Infinity;
          this.slides.forEach((_, i) => {
            const d = Math.abs(this.target(i) - offset);
            if (d < dist) {
              dist = d;
              best = i;
            }
          });
          return best;
        }

        go(delta) {
          if (!this.opts || this.opts.disabled) return;
          let next = this.nearest(this.offset) + delta;
          if (!this.loop) {
            // At the end there is nothing more to reveal.
            if (delta > 0 && this.offset >= this.maxOffset - 0.5) return;
            next = Math.max(0, Math.min(this.slides.length - 1, next));
          }
          this.animateTo(this.target(next), next);
        }

        animateTo(offset, index) {
          cancelAnimationFrame(this.frame);
          const start = this.offset;
          const distance = offset - start;
          this.index = index;
          if (reducedMotion() || distance === 0) {
            this.offset = offset;
            return this.render();
          }
          const began = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - began) / DURATION);
            this.offset = start + distance * easeOutQuint(t);
            this.render();
            if (t < 1) this.frame = requestAnimationFrame(tick);
          };
          this.frame = requestAnimationFrame(tick);
        }

        initDrag() {
          let startX = 0;
          let startY = 0;
          let startOffset = 0;
          let lastX = 0;
          let lastTime = 0;
          let velocity = 0;
          let dragging = false;
          let moved = false;
          let horizontal = null;

          this.track.addEventListener('pointerdown', (event) => {
            if (event.button !== 0 || !this.opts || this.opts.disabled) return;
            dragging = true;
            moved = false;
            horizontal = null;
            startX = lastX = event.clientX;
            startY = event.clientY;
            lastTime = performance.now();
            startOffset = this.offset;
            velocity = 0;
            cancelAnimationFrame(this.frame);
          });
          window.addEventListener('pointermove', (event) => {
            if (!dragging) return;
            const dx = event.clientX - startX;
            const dy = event.clientY - startY;
            if (horizontal === null && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) horizontal = Math.abs(dx) > Math.abs(dy);
            if (!horizontal) return;
            moved = true;
            const now = performance.now();
            velocity = (event.clientX - lastX) / Math.max(1, now - lastTime);
            lastX = event.clientX;
            lastTime = now;
            let next = startOffset - dx;
            if (!this.loop) {
              // Rubber band past either end.
              if (next < 0) next /= 3;
              if (next > this.maxOffset) next = this.maxOffset + (next - this.maxOffset) / 3;
            }
            this.offset = next;
            this.render();
          });
          window.addEventListener('pointerup', () => {
            if (!dragging) return;
            dragging = false;
            if (!moved) return;
            let index = this.nearest(this.offset);
            const startIndex = this.nearest(startOffset);
            if (Math.abs(velocity) > 0.3 && index === startIndex) index += velocity < 0 ? 1 : -1;
            if (!this.loop) index = Math.max(0, Math.min(this.slides.length - 1, index));
            this.animateTo(this.target(index), index);
          });
          this.track.addEventListener(
            'click',
            (event) => {
              if (moved) {
                event.preventDefault();
                event.stopPropagation();
                moved = false;
              }
            },
            true,
          );
          this.track.addEventListener('dragstart', (event) => event.preventDefault());
        }
      },
    );
  }

  // FAQ / care guide: one topic visible at a time, chosen from the side tabs or the mobile list.
  if (!customElements.get('custom-page-faq')) {
    customElements.define(
      'custom-page-faq',
      class CustomPageFaq extends HTMLElement {
        connectedCallback() {
          this.panels = Array.from(this.querySelectorAll('[data-cxp-panel]'));
          this.toggle = this.querySelector('[data-cxp-select-toggle]');
          this.collapse = this.querySelector('[data-cxp-select-panel]');
          this.label = this.querySelector('[data-cxp-select-label]');
          if (!this.panels.length) return;
          this.addEventListener('click', (event) => {
            const link = event.target.closest('[data-cxp-topic]');
            if (link) {
              event.preventDefault();
              this.show(link.dataset.cxpTopic, true);
              this.setOpen(false);
              return;
            }
            if (event.target.closest('[data-cxp-select-toggle]'))
              this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true');
          });
          this.onHash = () => {
            const id = decodeURIComponent(window.location.hash.slice(1));
            if (this.panels.some((panel) => panel.dataset.cxpPanel === id)) this.show(id, false);
          };
          window.addEventListener('hashchange', this.onHash);
          this.onHash();
        }

        disconnectedCallback() {
          window.removeEventListener('hashchange', this.onHash);
        }

        show(id, updateHash) {
          this.panels.forEach((panel) => (panel.hidden = panel.dataset.cxpPanel !== id));
          this.querySelectorAll('[data-cxp-topic]').forEach((link) => {
            if (link.dataset.cxpTopic === id) link.setAttribute('aria-current', 'true');
            else link.removeAttribute('aria-current');
          });
          const panel = this.panels.find((p) => p.dataset.cxpPanel === id);
          if (this.label && panel) this.label.textContent = panel.dataset.cxpTitle;
          if (updateHash) window.history.replaceState({}, '', `#${encodeURIComponent(id)}`);
        }

        setOpen(open) {
          if (!this.toggle || !this.collapse) return;
          this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          this.collapse.classList.toggle('is-open', open);
        }
      },
    );
  }

  // Press quotes: cross-fade, looping prev/next and an "n / total" counter.
  if (!customElements.get('custom-page-press')) {
    customElements.define(
      'custom-page-press',
      class CustomPagePress extends HTMLElement {
        connectedCallback() {
          this.quotes = Array.from(this.querySelectorAll('[data-cxp-quote]'));
          this.logos = Array.from(this.querySelectorAll('[data-cxp-logo]'));
          this.index = 0;
          this.querySelectorAll('[data-cxp-prev]').forEach((b) => b.addEventListener('click', () => this.go(-1)));
          this.querySelectorAll('[data-cxp-next]').forEach((b) => b.addEventListener('click', () => this.go(1)));
        }

        go(delta) {
          const count = this.quotes.length;
          if (count < 2) return;
          this.index = (((this.index + delta) % count) + count) % count;
          this.quotes.forEach((q, i) => {
            q.classList.toggle('is-active', i === this.index);
            q.setAttribute('aria-hidden', i === this.index ? 'false' : 'true');
          });
          this.logos.forEach((l, i) => l.classList.toggle('is-active', i === this.index));
          this.querySelectorAll('[data-cxp-current]').forEach((c) => (c.textContent = this.index + 1));
        }
      },
    );
  }

  // Video: swap the poster for the player on demand.
  if (!customElements.get('custom-page-video')) {
    customElements.define(
      'custom-page-video',
      class CustomPageVideo extends HTMLElement {
        connectedCallback() {
          this.querySelector('[data-cxp-play]')?.addEventListener('click', () => this.play());
        }

        play() {
          const template = this.querySelector('template');
          const player = this.querySelector('[data-cxp-player]');
          if (!template || !player) return;
          player.appendChild(template.content.cloneNode(true));
          this.classList.add('is-playing');
          const video = player.querySelector('video');
          if (video) {
            video.play?.().catch(() => {});
            video.focus();
          } else {
            player.querySelector('iframe')?.focus();
          }
        }
      },
    );
  }
})();
